import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Checkbox } from '@/components/Checkbox';
import { hasDecision, setConsent, onOpenConsent } from '@/lib/cookie-consent';
import { logConsent } from '@/lib/db/consent';

import './CookieConsent.css';

// First-visit consent banner (Spec M13.2): 3 categories (Essential locked on,
// Analytics, Marketing) and 3 equally-weighted actions (Accept all / Customize /
// Reject all). The choice persists in the ddm_consent cookie (13 months) and is
// logged server-side with IP + user-agent. Re-openable from the footer.
export function CookieConsent() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [customizing, setCustomizing] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    if (!hasDecision()) setVisible(true);
    return onOpenConsent(() => {
      setCustomizing(false);
      setVisible(true);
    });
  }, []);

  if (!visible) return null;

  const decide = (a: boolean, m: boolean) => {
    setConsent(a, m);
    void logConsent(a, m);
    setVisible(false);
    setCustomizing(false);
  };

  return (
    <div className="cookie-consent" role="region" aria-label={t('cookieConsent.title')}>
      <div className="cookie-consent__inner">
        <div className="cookie-consent__text">
          <h2 className="cookie-consent__title">{t('cookieConsent.title')}</h2>
          <p className="cookie-consent__desc">
            {t('cookieConsent.description')}{' '}
            <Link to="/legal/cookies" className="cookie-consent__link">
              {t('cookieConsent.learnMore')}
            </Link>
          </p>

          {customizing && (
            <div className="cookie-consent__categories">
              <Checkbox
                checked
                readOnly
                disabled
                label={
                  <span className="cookie-consent__cat">
                    <strong>{t('cookieConsent.categories.essential.label')}</strong>
                    <span className="cookie-consent__cat-desc">
                      {t('cookieConsent.categories.essential.description')}
                    </span>
                  </span>
                }
              />
              <Checkbox
                name="consent-analytics"
                checked={analytics}
                onChange={(e) => setAnalytics(e.target.checked)}
                label={
                  <span className="cookie-consent__cat">
                    <strong>{t('cookieConsent.categories.analytics.label')}</strong>
                    <span className="cookie-consent__cat-desc">
                      {t('cookieConsent.categories.analytics.description')}
                    </span>
                  </span>
                }
              />
              <Checkbox
                name="consent-marketing"
                checked={marketing}
                onChange={(e) => setMarketing(e.target.checked)}
                label={
                  <span className="cookie-consent__cat">
                    <strong>{t('cookieConsent.categories.marketing.label')}</strong>
                    <span className="cookie-consent__cat-desc">
                      {t('cookieConsent.categories.marketing.description')}
                    </span>
                  </span>
                }
              />
            </div>
          )}
        </div>

        <div className="cookie-consent__actions">
          {customizing ? (
            <>
              <button
                type="button"
                className="cookie-consent__btn"
                onClick={() => decide(false, false)}
              >
                {t('cookieConsent.rejectAll')}
              </button>
              <button
                type="button"
                className="cookie-consent__btn cookie-consent__btn--primary"
                onClick={() => decide(analytics, marketing)}
              >
                {t('cookieConsent.save')}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="cookie-consent__btn"
                onClick={() => decide(true, true)}
              >
                {t('cookieConsent.acceptAll')}
              </button>
              <button
                type="button"
                className="cookie-consent__btn"
                onClick={() => setCustomizing(true)}
              >
                {t('cookieConsent.customize')}
              </button>
              <button
                type="button"
                className="cookie-consent__btn"
                onClick={() => decide(false, false)}
              >
                {t('cookieConsent.rejectAll')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
